import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  ArrowLeft,
  CheckCircle,
  WarningCircle,
  XCircle,
  PaperPlaneTilt,
  Clock,
} from "@phosphor-icons/react";
import { Toaster, toast } from "sonner";

const STATUS_PILLS = {
  queued:      { cls: "bg-zinc-100 text-zinc-700",                  Icon: Clock },
  sent:        { cls: "bg-blue-50 text-blue-700 border border-blue-200",       Icon: PaperPlaneTilt },
  delivered:   { cls: "bg-emerald-50 text-emerald-700 border border-emerald-200", Icon: CheckCircle },
  bounced:     { cls: "bg-amber-50 text-amber-700 border border-amber-200",    Icon: WarningCircle },
  failed:      { cls: "bg-red-50 text-red-700 border border-red-200",          Icon: XCircle },
  unsubscribed:{ cls: "bg-zinc-100 text-zinc-700 border border-zinc-200",      Icon: XCircle },
};

export default function CampaignDetail() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/campaigns/${id}`);
      setData(data);
    } catch (e) {
      toast.error("Failed to load campaign");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  if (loading) return <div className="p-8 text-zinc-500">Loading…</div>;
  if (!data) return <div className="p-8 text-zinc-500">Campaign not found.</div>;

  const c = data.campaign;
  const recipients = (data.recipients || []).filter(r => {
    if (filter !== "all" && r.status !== filter) return false;
    if (q && !(r.email?.toLowerCase().includes(q.toLowerCase()) || r.name?.toLowerCase().includes(q.toLowerCase()))) return false;
    return true;
  });

  const stats = c.stats || {};
  const denom = c.recipient_count || 1;
  const deliveredPct = Math.round(((stats.delivered || 0) / denom) * 100);
  const unsubscribedCount = stats.unsubscribed || 0;
  // Percentage of unsubscribes over "successfully reached" recipients — falls
  // back to recipient_count if delivered isn't tracked yet, so the % is
  // never divide-by-zero for older campaigns.
  const unsubDenom = stats.delivered || stats.sent || c.recipient_count || 1;
  const unsubscribedPct = Math.round((unsubscribedCount / unsubDenom) * 100);

  return (
    <div className="p-6 sm:p-8 max-w-[1400px]" data-testid="campaign-detail">
      <Toaster position="top-right" />

      <Link to="/communication" className="inline-flex items-center gap-1.5 text-sm text-zinc-600 hover:text-zinc-900 mb-4">
        <ArrowLeft size={14} weight="bold" /> Back to Communication
      </Link>

      <div className="mb-6">
        <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-zinc-500">
          Campaign
        </div>
        <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight mt-1" style={{ fontFamily: "Work Sans" }}>
          {c.name}
        </h1>
        <div className="text-sm text-zinc-500 mt-1">
          Sent {c.sent_at ? new Date(c.sent_at).toLocaleString() : "—"}
          {c.sent_by_name && <> by {c.sent_by_name}</>}
          {" · "}<span className="capitalize">{c.audience}</span> audience
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
        <StatCard label="Recipients" value={c.recipient_count} tone="neutral" />
        <StatCard label="Sent" value={stats.sent || 0} tone="info" />
        <StatCard label="Delivered" value={stats.delivered || 0} tone="success" sub={`${deliveredPct}%`} />
        <StatCard label="Bounced" value={stats.bounced || 0} tone="warn" />
        <StatCard label="Failed" value={stats.failed || 0} tone="error" />
        <StatCard
          label="Unsubscribed"
          value={unsubscribedCount}
          tone="neutral"
          sub={`${unsubscribedPct}%`}
          data-testid="stat-unsubscribed"
        />
      </div>

      <div className="bg-white border border-zinc-200 rounded-md p-4 mb-4">
        <div className="text-[10px] font-semibold uppercase tracking-wider text-zinc-500 mb-1.5">Subject</div>
        <div className="font-semibold text-zinc-900 mb-3">{c.subject}</div>
        <div className="text-[10px] font-semibold uppercase tracking-wider text-zinc-500 mb-1.5">Body preview</div>
        <div className="border border-zinc-200 rounded p-3 bg-zinc-50 text-sm max-h-72 overflow-auto"
             dangerouslySetInnerHTML={{ __html: c.body_html }} />
      </div>

      <div className="flex flex-col sm:flex-row gap-3 mb-3">
        <Input
          placeholder="Search by email or name"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className="max-w-sm"
          data-testid="recipient-search"
        />
        <div className="flex gap-1 flex-wrap">
          {["all", "sent", "delivered", "bounced", "failed", "unsubscribed"].map((s) => (
            <button
              key={s}
              onClick={() => setFilter(s)}
              className={`px-3 py-1.5 text-xs font-medium rounded ${
                filter === s ? "bg-zinc-900 text-white" : "bg-zinc-100 text-zinc-700 hover:bg-zinc-200"
              }`}
              data-testid={`filter-${s}`}
            >
              {s} {s !== "all" && `(${stats[s] || 0})`}
            </button>
          ))}
        </div>
      </div>

      <div className="bg-white border border-zinc-200 rounded-md overflow-x-auto">
        <table className="w-full text-sm min-w-[680px]" data-testid="recipients-table">
          <thead className="bg-zinc-50 border-b border-zinc-200">
            <tr className="text-left text-xs uppercase tracking-wider text-zinc-500">
              <th className="px-5 py-3 font-semibold">Recipient</th>
              <th className="px-5 py-3 font-semibold">Email</th>
              <th className="px-5 py-3 font-semibold">Status</th>
              <th className="px-5 py-3 font-semibold">Sent at</th>
              <th className="px-5 py-3 font-semibold">Detail</th>
            </tr>
          </thead>
          <tbody>
            {recipients.length === 0 ? (
              <tr><td colSpan={5} className="px-5 py-10 text-center text-zinc-500">No matching recipients.</td></tr>
            ) : recipients.map((r) => {
              const pill = STATUS_PILLS[r.status] || STATUS_PILLS.sent;
              const I = pill.Icon;
              return (
                <tr key={r.id} className="border-b border-zinc-100 last:border-0" data-testid={`recipient-${r.id}`}>
                  <td className="px-5 py-3 font-medium text-zinc-900">{r.name || "—"}</td>
                  <td className="px-5 py-3 text-zinc-700">{r.email}</td>
                  <td className="px-5 py-3">
                    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold ${pill.cls}`}>
                      <I size={11} weight="bold" /> {r.status}
                    </span>
                  </td>
                  <td className="px-5 py-3 text-xs text-zinc-600 whitespace-nowrap">
                    {r.sent_at ? new Date(r.sent_at).toLocaleString() : "—"}
                  </td>
                  <td className="px-5 py-3 text-xs text-zinc-600 max-w-[260px] truncate" title={r.error || r.resend_id || ""}>
                    {r.error || r.resend_id || "—"}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function StatCard({ label, value, sub, tone }) {
  const tones = {
    neutral: "border-zinc-200 bg-white",
    info: "border-blue-200 bg-blue-50",
    success: "border-emerald-200 bg-emerald-50",
    warn: "border-amber-200 bg-amber-50",
    error: "border-red-200 bg-red-50",
  }[tone] || "border-zinc-200 bg-white";
  return (
    <div className={`border rounded-md p-4 ${tones}`}>
      <div className="text-[10px] font-semibold uppercase tracking-wider text-zinc-600">{label}</div>
      <div className="text-2xl font-semibold mt-0.5">{value}</div>
      {sub && <div className="text-xs text-zinc-500">{sub}</div>}
    </div>
  );
}
